/**
 * Company: SPHMedia
 * Description: [Reaction toolbar hook]
 */

import { HandleReactionClick } from "@typings/Reactions";
import { useCallback, useEffect, useState } from "react";

type useReactionToolbarData = {
  isReactionExpanded: boolean;
  toggleExpand: () => void;
  handleReactionSelect: HandleReactionClick;
};

type useReactionToolbarProps = {
  articleId: string;
  handleReactionClick: HandleReactionClick;
  isExpanded?: boolean;
  onExpand?: (articleId: string | null) => void;
};

/**
 * Custom React Hook: useReactionToolbar
 * Description: [Hooks for reaction toolbar]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useReactionToolbar({
  articleId,
  handleReactionClick,
  isExpanded = false,
  onExpand,
}: useReactionToolbarProps): useReactionToolbarData {
  const [isReactionExpanded, setIsReactionExpanded] = useState(isExpanded);

  const toggleExpand = useCallback(() => {
    const newState = !isReactionExpanded;
    setIsReactionExpanded(newState);
    onExpand?.(newState ? articleId : null);
  }, [articleId, isReactionExpanded, onExpand]);

  const handleReactionSelect = useCallback(
    async (reactionId: string, variant?: "top" | "bottom") => {
      await handleReactionClick(reactionId, variant);
      // Collapse after selecting
      setIsReactionExpanded(false);
      onExpand?.(null);
    },
    [handleReactionClick, onExpand],
  );

  // Sync local state with parent prop
  useEffect(() => {
    setIsReactionExpanded(isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const el = event.target as HTMLElement;
      if (!(
        el.classList.contains("interaction-toolbar") ||
        el.closest(".interaction-toolbar")
      )) {
        setIsReactionExpanded(false);
        onExpand?.(null);
      }
    };

    globalThis.addEventListener("mouseup", handleOutsideClick);
    globalThis.addEventListener("touchend", handleOutsideClick);

    return () => {
      globalThis.removeEventListener("mouseup", handleOutsideClick);
      globalThis.removeEventListener("touchend", handleOutsideClick);
    };
  }, [onExpand]);

  return {
    isReactionExpanded,
    toggleExpand,
    handleReactionSelect,
  };
}
